import React from "react";

export var ROOT_FOCUS_KEY = "SN:ROOT";

var KEY_LEFT = "left";
var KEY_RIGHT = "right";
var KEY_UP = "up";
var KEY_DOWN = "down";
var KEY_ENTER = "enter";

var DEFAULT_KEY_MAP = {
  left: [37, "ArrowLeft", "Left"],
  up: [38, "ArrowUp", "Up"],
  right: [39, "ArrowRight", "Right"],
  down: [40, "ArrowDown", "Down"],
  enter: [13, "Enter", "NumpadEnter", "Select"],
};

var CONTENT_AREA_CLASS_NAMES = ["legacy-app-shell-content", "legacy-login-content-cell"];

var focusKeyCounter = 0;

function noop() {}

function defaultArrowPress() {
  return true;
}

function isArray(value) {
  return Object.prototype.toString.call(value) === "[object Array]";
}

function uniqueFocusKey() {
  focusKeyCounter += 1;
  return "mnb:focusable-" + focusKeyCounter;
}

function copyArray(value) {
  return value ? value.slice(0) : [];
}

function indexOf(list, value) {
  if (!list) return -1;
  for (var i = 0; i < list.length; i += 1) {
    if (list[i] === value) return i;
  }
  return -1;
}

function contains(list, value) {
  return indexOf(list, value) !== -1;
}

function normalizeKeyList(value) {
  if (isArray(value)) return value;
  return [value];
}

function createLayout(node) {
  var layout = {
    x: 0,
    y: 0,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    node: node || null,
  };

  layout.right = 0;
  layout.bottom = 0;
  return layout;
}

function getOffsetLayout(node) {
  var left = 0;
  var top = 0;
  var width = node ? node.offsetWidth || 0 : 0;
  var height = node ? node.offsetHeight || 0 : 0;
  var current = node;

  while (current) {
    left += current.offsetLeft || 0;
    top += current.offsetTop || 0;
    if (current.scrollLeft) left -= current.scrollLeft;
    if (current.scrollTop) top -= current.scrollTop;
    current = current.offsetParent;
  }

  return {
    x: left,
    y: top,
    left: left,
    top: top,
    right: left + width,
    bottom: top + height,
    width: width,
    height: height,
    node: node,
  };
}

function getNodeLayout(node) {
  if (!node) return createLayout(null);

  if (node.getBoundingClientRect) {
    var rect = node.getBoundingClientRect();
    var left = rect.left || rect.x || 0;
    var top = rect.top || rect.y || 0;
    var width = rect.width;
    var height = rect.height;

    if (width == null) width = (rect.right || 0) - left;
    if (height == null) height = (rect.bottom || 0) - top;

    return {
      x: left,
      y: top,
      left: left,
      top: top,
      right: left + width,
      bottom: top + height,
      width: width,
      height: height,
      node: node,
    };
  }

  return getOffsetLayout(node);
}

function isDisabledNode(node) {
  if (!node) return true;
  if (node.disabled) return true;
  if (node.getAttribute) {
    if (node.getAttribute("aria-disabled") === "true") return true;
    if (node.getAttribute("disabled") != null) return true;
  }
  return false;
}

function isVisibleNode(node, layout) {
  if (!node) return false;
  if (isDisabledNode(node)) return false;

  if (typeof window !== "undefined" && window.getComputedStyle) {
    var style = window.getComputedStyle(node);
    if (style && (style.display === "none" || style.visibility === "hidden")) {
      return false;
    }
  }

  if (layout && (layout.width > 0 || layout.height > 0)) return true;
  if (node.offsetWidth > 0 || node.offsetHeight > 0) return true;
  if (node.getClientRects && node.getClientRects().length > 0) return true;

  return false;
}

function centerX(layout) {
  return layout.left + layout.width / 2;
}

function centerY(layout) {
  return layout.top + layout.height / 2;
}

function overlapAmount(aStart, aEnd, bStart, bEnd) {
  var start = Math.max(aStart, bStart);
  var end = Math.min(aEnd, bEnd);
  return Math.max(0, end - start);
}

function getPrimaryDistance(direction, fromLayout, toLayout) {
  if (direction === KEY_LEFT) {
    return fromLayout.left - toLayout.right;
  }
  if (direction === KEY_RIGHT) {
    return toLayout.left - fromLayout.right;
  }
  if (direction === KEY_UP) {
    return fromLayout.top - toLayout.bottom;
  }
  return toLayout.top - fromLayout.bottom;
}

function isInDirection(direction, fromLayout, toLayout) {
  var fromCenterX = centerX(fromLayout);
  var fromCenterY = centerY(fromLayout);
  var toCenterX = centerX(toLayout);
  var toCenterY = centerY(toLayout);

  if (direction === KEY_LEFT) return toCenterX < fromCenterX - 1;
  if (direction === KEY_RIGHT) return toCenterX > fromCenterX + 1;
  if (direction === KEY_UP) return toCenterY < fromCenterY - 1;
  return toCenterY > fromCenterY + 1;
}

function scoreCandidate(direction, fromLayout, toLayout) {
  var vertical = direction === KEY_UP || direction === KEY_DOWN;
  var overlap = vertical
    ? overlapAmount(fromLayout.left, fromLayout.right, toLayout.left, toLayout.right)
    : overlapAmount(fromLayout.top, fromLayout.bottom, toLayout.top, toLayout.bottom);
  var primary = getPrimaryDistance(direction, fromLayout, toLayout);
  var secondary = vertical
    ? Math.abs(centerX(fromLayout) - centerX(toLayout))
    : Math.abs(centerY(fromLayout) - centerY(toLayout));

  if (primary < 0) primary = Math.abs(primary) / 2;
  return (overlap > 0 ? 0 : 100000) + primary * 5 + secondary;
}

function isSameHorizontalBand(fromLayout, toLayout) {
  var shortestHeight = Math.min(fromLayout.height || 0, toLayout.height || 0);

  if (shortestHeight <= 0) return false;

  return (
    overlapAmount(fromLayout.top, fromLayout.bottom, toLayout.top, toLayout.bottom) >=
    shortestHeight * 0.6
  );
}

function sortByLayoutOrder(items, rtl) {
  return items.sort(function (a, b) {
    var aLayout = a.layout || createLayout(a.node);
    var bLayout = b.layout || createLayout(b.node);
    var topDiff = aLayout.top - bLayout.top;
    if (Math.abs(topDiff) > 8) return topDiff;
    if (rtl) return bLayout.right - aLayout.right;
    return aLayout.left - bLayout.left;
  });
}

function getScrollableParent(node) {
  var current = node ? node.parentNode : null;

  while (current && current !== document.body && current !== document.documentElement) {
    var className = typeof current.className === "string" ? current.className : "";

    if (className.indexOf("legacy-route-scroll") !== -1) return current;
    if (
      current.scrollHeight &&
      current.clientHeight &&
      current.scrollHeight > current.clientHeight + 1
    ) {
      return current;
    }

    current = current.parentNode;
  }

  return null;
}

function isNodeInsideClass(node, targetClassName) {
  var current = node;

  while (current && current.nodeType === 1) {
    var className = typeof current.className === "string" ? current.className : "";
    if (className.indexOf(targetClassName) !== -1) return true;
    current = current.parentNode;
  }

  return false;
}

function isNodeInsideAnyClass(node, targetClassNames) {
  for (var i = 0; i < targetClassNames.length; i += 1) {
    if (isNodeInsideClass(node, targetClassNames[i])) return true;
  }

  return false;
}

function isNodeInsideContentArea(node) {
  return isNodeInsideAnyClass(node, CONTENT_AREA_CLASS_NAMES);
}

function getKeyCode(event) {
  if (!event) return null;
  if (event.keyCode) return event.keyCode;
  if (event.which) return event.which;
  if (event.code) return event.code;
  return event.key;
}

function SpatialNavigationService() {
  this.focusableComponents = {};
  this.focusKey = null;
  this.parentsHavingFocusedChild = [];
  this.enabled = false;
  this.paused = false;
  this.keyMap = DEFAULT_KEY_MAP;
  this.pressedKeys = {};
  this.rtl = false;
  this.debug = false;
  this.shouldFocusDOMNode = false;
  this.shouldUseNativeEvents = false;
  this.domNodeFocusOptions = {};
  this.pendingFocusKey = null;
  this.visibilityTimers = [];
  this.initialFocusTimer = null;
  this.lastContentFocusKey = null;
  this.keyDownEventListener = null;
  this.keyUpEventListener = null;

  this.init = this.init.bind(this);
  this.destroy = this.destroy.bind(this);
  this.setFocus = this.setFocus.bind(this);
  this.navigateByDirection = this.navigateByDirection.bind(this);
  this.updateAllLayouts = this.updateAllLayouts.bind(this);
  this.pause = this.pause.bind(this);
  this.resume = this.resume.bind(this);
}

SpatialNavigationService.prototype.log = function () {
  if (!this.debug || typeof console === "undefined" || !console.log) return;
  console.log.apply(console, arguments);
};

SpatialNavigationService.prototype.init = function (options) {
  var config = options || {};
  var wasEnabled = this.enabled;

  this.enabled = true;
  this.debug = !!config.debug;
  this.rtl = !!config.rtl;
  this.shouldFocusDOMNode = !!config.shouldFocusDOMNode;
  this.shouldUseNativeEvents = !!config.shouldUseNativeEvents;
  this.domNodeFocusOptions = config.domNodeFocusOptions || {};

  if (!wasEnabled) {
    this.bindEventHandlers();
  }

  this.scheduleInitialFocus();
};

SpatialNavigationService.prototype.destroy = function () {
  this.unbindEventHandlers();
  this.focusableComponents = {};
  this.focusKey = null;
  this.parentsHavingFocusedChild = [];
  this.enabled = false;
  this.paused = false;
  this.pressedKeys = {};
  this.pendingFocusKey = null;
  this.clearVisibilityTimers();
  this.clearInitialFocusTimer();
  this.lastContentFocusKey = null;
  this.keyMap = DEFAULT_KEY_MAP;
};

SpatialNavigationService.prototype.bindEventHandlers = function () {
  var self = this;

  if (typeof window === "undefined" || !window.addEventListener) return;
  if (this.keyDownEventListener) return;

  this.keyDownEventListener = function (event) {
    self.handleKeyDown(event);
  };
  this.keyUpEventListener = function (event) {
    self.handleKeyUp(event);
  };

  window.addEventListener("keydown", this.keyDownEventListener);
  window.addEventListener("keyup", this.keyUpEventListener);
};

SpatialNavigationService.prototype.unbindEventHandlers = function () {
  if (typeof window === "undefined" || !window.removeEventListener) return;

  if (this.keyDownEventListener) {
    window.removeEventListener("keydown", this.keyDownEventListener);
  }
  if (this.keyUpEventListener) {
    window.removeEventListener("keyup", this.keyUpEventListener);
  }

  this.keyDownEventListener = null;
  this.keyUpEventListener = null;
};

SpatialNavigationService.prototype.getEventType = function (keyCodeValue) {
  var map = this.keyMap;
  var names = Object.keys(map);

  for (var i = 0; i < names.length; i += 1) {
    var name = names[i];
    if (contains(map[name], keyCodeValue)) return name;
  }

  return null;
};

SpatialNavigationService.prototype.getEventTypeFromEvent = function (event) {
  var keyCodeValue = getKeyCode(event);
  return (
    this.getEventType(keyCodeValue) ||
    this.getEventType(event && event.key) ||
    this.getEventType(event && event.code)
  );
};

SpatialNavigationService.prototype.handleKeyDown = function (event) {
  if (this.paused || !this.enabled) return;

  var eventType = this.getEventTypeFromEvent(event);
  if (!eventType) return;

  this.pressedKeys[eventType] = this.pressedKeys[eventType]
    ? this.pressedKeys[eventType] + 1
    : 1;

  if (!this.shouldUseNativeEvents && event && event.preventDefault) {
    event.preventDefault();
  }

  var details = { pressedKeys: this.pressedKeys };

  if (eventType === KEY_ENTER) {
    this.onEnterPress(details);
    return;
  }

  if (this.onArrowPress(eventType, details) === false) {
    return;
  }

  this.smartNavigate(eventType, null, { event: event });
};

SpatialNavigationService.prototype.handleKeyUp = function (event) {
  var eventType = this.getEventTypeFromEvent(event);
  if (!eventType) return;

  delete this.pressedKeys[eventType];
  if (eventType === KEY_ENTER) this.onEnterRelease();
};

SpatialNavigationService.prototype.setKeyMap = function (keyMap) {
  var merged = {};
  var currentKeys = Object.keys(this.keyMap);
  var incomingKeys = keyMap ? Object.keys(keyMap) : [];
  var i;

  for (i = 0; i < currentKeys.length; i += 1) {
    merged[currentKeys[i]] = copyArray(this.keyMap[currentKeys[i]]);
  }

  for (i = 0; i < incomingKeys.length; i += 1) {
    merged[incomingKeys[i]] = normalizeKeyList(keyMap[incomingKeys[i]]);
  }

  this.keyMap = merged;
};

SpatialNavigationService.prototype.getKeyMap = function () {
  return this.keyMap;
};

SpatialNavigationService.prototype.addFocusable = function (component) {
  var focusKey = component.focusKey;
  var existing = this.focusableComponents[focusKey];

  this.focusableComponents[focusKey] = Object.assign(
    {
      focusKey: focusKey,
      node: null,
      parentFocusKey: ROOT_FOCUS_KEY,
      preferredChildFocusKey: null,
      onEnterPress: noop,
      onEnterRelease: noop,
      onArrowPress: defaultArrowPress,
      onFocus: noop,
      onBlur: noop,
      onUpdateFocus: noop,
      onUpdateHasFocusedChild: noop,
      saveLastFocusedChild: true,
      trackChildren: false,
      autoRestoreFocus: true,
      forceFocus: false,
      focusable: true,
      isFocusBoundary: false,
      focusBoundaryDirections: null,
      lastFocusedChildKey: existing ? existing.lastFocusedChildKey : null,
      layout: createLayout(component.node),
    },
    component
  );

  this.updateLayout(focusKey);

  if (focusKey === this.focusKey) {
    this.focusableComponents[focusKey].onUpdateFocus(true);
  }

  if (this.pendingFocusKey === focusKey) {
    this.pendingFocusKey = null;
    this.setFocus(focusKey);
  }

  if (!this.focusKey && this.focusableComponents[focusKey].forceFocus && this.isParticipatingFocusableComponent(focusKey)) {
    this.setFocus(focusKey);
  }

  this.scheduleInitialFocus();
};

SpatialNavigationService.prototype.updateFocusable = function (focusKey, update) {
  var component = this.focusableComponents[focusKey];
  if (!component) return;

  Object.assign(component, update);
  if (update.node) component.node = update.node;
  this.updateLayout(focusKey);

  if (this.pendingFocusKey === focusKey && this.isParticipatingFocusableComponent(focusKey)) {
    this.pendingFocusKey = null;
    this.setFocus(focusKey);
  }

  if (!this.focusKey && component.forceFocus && this.isParticipatingFocusableComponent(focusKey)) {
    this.setFocus(focusKey);
  }

  this.scheduleInitialFocus();
};

SpatialNavigationService.prototype.removeFocusable = function (payload) {
  var focusKey = payload.focusKey;
  var component = this.focusableComponents[focusKey];
  var parentKey = component ? component.parentFocusKey : null;
  var wasFocused = focusKey === this.focusKey;
  var hadFocusedChild = contains(this.parentsHavingFocusedChild, focusKey);

  if (!component) return;

  component.onUpdateFocus(false);
  component.onUpdateHasFocusedChild(false);
  delete this.focusableComponents[focusKey];

  if (wasFocused) this.focusKey = null;
  this.parentsHavingFocusedChild = this.parentsHavingFocusedChild.filter(function (key) {
    return key !== focusKey;
  });

  if ((wasFocused || hadFocusedChild) && parentKey) {
    var parent = this.focusableComponents[parentKey];
    if (parent && parent.autoRestoreFocus) {
      this.setFocus(parentKey);
    }
  }

  this.scheduleInitialFocus();
};

SpatialNavigationService.prototype.updateLayout = function (focusKey) {
  var component = this.focusableComponents[focusKey];
  if (!component) return null;

  component.layout = getNodeLayout(component.node);
  return component.layout;
};

SpatialNavigationService.prototype.updateAllLayouts = function () {
  var keys = Object.keys(this.focusableComponents);
  for (var i = 0; i < keys.length; i += 1) {
    this.updateLayout(keys[i]);
  }
};

SpatialNavigationService.prototype.getNodeLayoutByFocusKey = function (focusKey) {
  return this.updateLayout(focusKey);
};

SpatialNavigationService.prototype.isFocusableComponent = function (focusKey) {
  return !!this.focusableComponents[focusKey];
};

SpatialNavigationService.prototype.isParticipatingFocusableComponent = function (focusKey) {
  var component = this.focusableComponents[focusKey];
  if (!component || component.focusable === false) return false;

  this.updateLayout(focusKey);
  return isVisibleNode(component.node, component.layout);
};

SpatialNavigationService.prototype.hasParticipatingDescendant = function (parentFocusKey) {
  var keys = Object.keys(this.focusableComponents);

  for (var i = 0; i < keys.length; i += 1) {
    var key = keys[i];
    var component = this.focusableComponents[key];
    if (component.parentFocusKey !== parentFocusKey) continue;

    if (this.isParticipatingFocusableComponent(key)) return true;
    if (component.trackChildren && this.hasParticipatingDescendant(key)) return true;
  }

  return false;
};

SpatialNavigationService.prototype.isParticipatingNavigationComponent = function (focusKey) {
  var component = this.focusableComponents[focusKey];
  if (!component) return false;
  if (component.focusable === false && !component.trackChildren) return false;
  if (component.focusable === false && !this.hasParticipatingDescendant(focusKey)) return false;

  this.updateLayout(focusKey);
  return isVisibleNode(component.node, component.layout);
};

SpatialNavigationService.prototype.doesFocusableExist = function (focusKey) {
  return this.isFocusableComponent(focusKey);
};

SpatialNavigationService.prototype.getCurrentFocusKey = function () {
  return this.focusKey;
};

SpatialNavigationService.prototype.getDirectChildren = function (parentFocusKey, onlyParticipating) {
  var keys = Object.keys(this.focusableComponents);
  var children = [];

  for (var i = 0; i < keys.length; i += 1) {
    var key = keys[i];
    var component = this.focusableComponents[key];
    if (component.parentFocusKey !== parentFocusKey) continue;
    if (onlyParticipating && !this.isParticipatingNavigationComponent(key)) continue;
    children.push(component);
  }

  return children;
};

SpatialNavigationService.prototype.getForcedFocusKey = function () {
  var keys = Object.keys(this.focusableComponents);
  var forced = [];

  for (var i = 0; i < keys.length; i += 1) {
    var component = this.focusableComponents[keys[i]];
    if (component.forceFocus && this.isParticipatingFocusableComponent(component.focusKey)) {
      forced.push(component);
    }
  }

  sortByLayoutOrder(forced, this.rtl);
  return forced.length ? forced[0].focusKey : null;
};

SpatialNavigationService.prototype.getFirstChildFocusKey = function (parentFocusKey) {
  var children = this.getDirectChildren(parentFocusKey, true);
  if (!children.length) return null;

  sortByLayoutOrder(children, this.rtl);
  return children[0].focusKey;
};

SpatialNavigationService.prototype.getNextFocusKey = function (targetFocusKey) {
  var target = this.focusableComponents[targetFocusKey];
  var nextKey;

  if (!target) return targetFocusKey;

  if (target.saveLastFocusedChild && target.lastFocusedChildKey) {
    nextKey = target.lastFocusedChildKey;
    if (this.isParticipatingFocusableComponent(nextKey)) {
      return this.getNextFocusKey(nextKey);
    }
  }

  if (target.preferredChildFocusKey) {
    nextKey = target.preferredChildFocusKey;
    if (this.isParticipatingFocusableComponent(nextKey)) {
      return this.getNextFocusKey(nextKey);
    }
  }

  nextKey = this.getFirstChildFocusKey(targetFocusKey);
  if (nextKey) return this.getNextFocusKey(nextKey);

  return targetFocusKey;
};

SpatialNavigationService.prototype.getParticipatingFocusableDescendants = function (
  parentFocusKey,
  results
) {
  var descendants = results || [];
  var children = this.getDirectChildren(parentFocusKey, false);

  for (var i = 0; i < children.length; i += 1) {
    var child = children[i];

    if (this.isParticipatingFocusableComponent(child.focusKey)) {
      descendants.push(child);
    } else if (this.getDirectChildren(child.focusKey, false).length) {
      this.getParticipatingFocusableDescendants(child.focusKey, descendants);
    }
  }

  return descendants;
};

SpatialNavigationService.prototype.findDirectionalDescendant = function (
  parentFocusKey,
  direction,
  fromLayout
) {
  var descendants = this.getParticipatingFocusableDescendants(parentFocusKey, []);
  var candidates = [];
  var fallbackCandidates = [];

  for (var i = 0; i < descendants.length; i += 1) {
    var descendant = descendants[i];
    var layout = this.updateLayout(descendant.focusKey);
    var candidate = {
      component: descendant,
      score: scoreCandidate(direction, fromLayout, layout),
    };

    fallbackCandidates.push(candidate);
    if (isInDirection(direction, fromLayout, layout)) candidates.push(candidate);
  }

  if (!candidates.length) candidates = fallbackCandidates;
  candidates.sort(function (a, b) {
    return a.score - b.score;
  });

  return candidates.length ? candidates[0].component : null;
};

SpatialNavigationService.prototype.getAncestors = function (focusKey) {
  var ancestors = [];
  var component = this.focusableComponents[focusKey];

  while (component) {
    var parentKey = component.parentFocusKey;
    if (!parentKey || parentKey === ROOT_FOCUS_KEY) break;
    ancestors.push(parentKey);
    component = this.focusableComponents[parentKey];
  }

  return ancestors;
};

SpatialNavigationService.prototype.updateParentsHasFocusedChild = function (focusKey, details) {
  var nextParents = this.getAncestors(focusKey);
  var previousParents = this.parentsHavingFocusedChild;
  var i;

  for (i = 0; i < previousParents.length; i += 1) {
    if (!contains(nextParents, previousParents[i])) {
      var blurredParent = this.focusableComponents[previousParents[i]];
      if (blurredParent) {
        if (blurredParent.trackChildren) blurredParent.onUpdateHasFocusedChild(false);
        if (blurredParent.focusable !== false) {
          blurredParent.onBlur(this.getNodeLayoutByFocusKey(blurredParent.focusKey), details || {});
        }
      }
    }
  }

  for (i = 0; i < nextParents.length; i += 1) {
    if (!contains(previousParents, nextParents[i])) {
      var focusedParent = this.focusableComponents[nextParents[i]];
      if (focusedParent) {
        if (focusedParent.trackChildren) focusedParent.onUpdateHasFocusedChild(true);
        if (focusedParent.focusable !== false) {
          focusedParent.onFocus(this.getNodeLayoutByFocusKey(focusedParent.focusKey), details || {});
        }
      }
    }
  }

  this.parentsHavingFocusedChild = nextParents;
};

SpatialNavigationService.prototype.updateParentsLastFocusedChild = function (focusKey) {
  var childKey = focusKey;
  var component = this.focusableComponents[focusKey];

  while (component) {
    var parentKey = component.parentFocusKey;
    var parent = this.focusableComponents[parentKey];
    if (!parent) break;
    parent.lastFocusedChildKey = childKey;
    childKey = parent.focusKey;
    component = parent;
  }
};

SpatialNavigationService.prototype.clearFocusedNodeMarkers = function (nextNode) {
  if (typeof document === "undefined" || !document.querySelectorAll) return;

  var nodes = document.querySelectorAll('[data-spatial-focused="true"]');
  for (var i = 0; i < nodes.length; i += 1) {
    if (nodes[i] !== nextNode && nodes[i].removeAttribute) {
      nodes[i].removeAttribute("data-spatial-focused");
    }
  }
};

SpatialNavigationService.prototype.ensureNodeVisible = function (node) {
  if (!node || !node.getBoundingClientRect) return;

  var scrollParent = getScrollableParent(node);
  if (!scrollParent || !scrollParent.getBoundingClientRect) return;
  var scrollParentClassName =
    typeof scrollParent.className === "string" ? scrollParent.className : "";

  if (scrollParentClassName.indexOf("legacy-home-route-scroll") !== -1) return;
  if (scrollParentClassName.indexOf("legacy-detail-scroll") !== -1) return;

  var nodeRect = node.getBoundingClientRect();
  var parentRect = scrollParent.getBoundingClientRect();
  var topPadding = 0;
  var bottomPadding = 24;

  if (nodeRect.top < parentRect.top + topPadding) {
    scrollParent.scrollTop += nodeRect.top - parentRect.top - topPadding;
  } else if (nodeRect.bottom > parentRect.bottom - bottomPadding) {
    scrollParent.scrollTop += nodeRect.bottom - parentRect.bottom + bottomPadding;
  }
};

SpatialNavigationService.prototype.clearVisibilityTimers = function () {
  for (var i = 0; i < this.visibilityTimers.length; i += 1) {
    clearTimeout(this.visibilityTimers[i]);
  }
  this.visibilityTimers = [];
};

SpatialNavigationService.prototype.clearInitialFocusTimer = function () {
  if (this.initialFocusTimer) clearTimeout(this.initialFocusTimer);
  this.initialFocusTimer = null;
};

SpatialNavigationService.prototype.getInitialFocusKey = function () {
  var forcedFocusKey = this.getForcedFocusKey();
  var homeTopSliderNode;
  var homeTopSliderFocusKey;

  if (forcedFocusKey) return forcedFocusKey;

  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.pathname === "/" &&
    typeof document !== "undefined" &&
    document.querySelector &&
    document.querySelector(".legacy-home-route-scroll")
  ) {
    homeTopSliderNode = document.querySelector("[data-home-top-slider-focus-key]");
    homeTopSliderFocusKey =
      homeTopSliderNode && homeTopSliderNode.getAttribute
        ? homeTopSliderNode.getAttribute("data-home-top-slider-focus-key")
        : null;

    if (homeTopSliderFocusKey && this.isFocusableComponent(homeTopSliderFocusKey)) {
      return homeTopSliderFocusKey;
    }

    return null;
  }

  var contentFocusKey = this.getContentFocusKey();
  if (contentFocusKey) return contentFocusKey;

  if (typeof document !== "undefined" && document.querySelector) {
    var shellContent = document.querySelector(".legacy-app-shell-content, .legacy-login-content-cell");
    if (shellContent) return null;
  }

  return this.getFirstChildFocusKey(ROOT_FOCUS_KEY);
};

SpatialNavigationService.prototype.scheduleInitialFocus = function () {
  var self = this;

  if (!this.enabled || this.paused || this.focusKey || this.initialFocusTimer) return;

  this.initialFocusTimer = setTimeout(function () {
    self.initialFocusTimer = null;
    if (self.focusKey || !self.enabled || self.paused) return;

    var nextFocusKey = self.getInitialFocusKey();
    if (nextFocusKey) self.setFocus(nextFocusKey);
  }, 60);
};

SpatialNavigationService.prototype.scheduleEnsureNodeVisible = function (focusKey) {
  var self = this;
  var delays = [0, 100, 300, 900, 2000];

  this.clearVisibilityTimers();

  for (var i = 0; i < delays.length; i += 1) {
    this.visibilityTimers.push(setTimeout(function () {
      var component = self.focusableComponents[focusKey];
      if (self.focusKey === focusKey && component) {
        self.ensureNodeVisible(component.node);
      }
    }, delays[i]));
  }
};

SpatialNavigationService.prototype.setCurrentFocusedKey = function (newFocusKey, details) {
  var previousKey = this.focusKey;
  var previous = this.focusableComponents[previousKey];
  var next = this.focusableComponents[newFocusKey];
  var focusDetails = details || {};

  if (!next) return;
  this.clearFocusedNodeMarkers(next.node);

  if (previous && previousKey !== newFocusKey) {
    previous.onUpdateFocus(false);
    previous.onBlur(this.getNodeLayoutByFocusKey(previousKey), focusDetails);
    if (previous.node && previous.node.removeAttribute) {
      previous.node.removeAttribute("data-spatial-focused");
    }
  }

  this.focusKey = newFocusKey;
  if (isNodeInsideContentArea(next.node)) {
    this.lastContentFocusKey = newFocusKey;
  }
  next.onUpdateFocus(true);
  if (next.node && next.node.setAttribute) {
    next.node.setAttribute("data-spatial-focused", "true");
  }

  if (this.shouldFocusDOMNode && next.node && next.node.focus) {
    try {
      next.node.focus(this.domNodeFocusOptions);
    } catch (error) {
      next.node.focus();
    }
  }

  next.onFocus(this.getNodeLayoutByFocusKey(newFocusKey), focusDetails);
};

SpatialNavigationService.prototype.setFocus = function (focusKey, focusDetails) {
  var targetKey = focusKey;

  if (!this.enabled) this.init();

  if (!targetKey || targetKey === ROOT_FOCUS_KEY) {
    targetKey = this.getForcedFocusKey() || this.getFirstChildFocusKey(ROOT_FOCUS_KEY);
  }

  if (!targetKey || !this.focusableComponents[targetKey]) {
    this.pendingFocusKey = targetKey || null;
    return;
  }

  var nextFocusKey = this.getNextFocusKey(targetKey);

  if (!this.isParticipatingFocusableComponent(nextFocusKey)) {
    this.pendingFocusKey = targetKey;
    return;
  }

  this.pendingFocusKey = null;
  this.clearInitialFocusTimer();
  this.setCurrentFocusedKey(nextFocusKey, focusDetails || {});
  this.updateParentsHasFocusedChild(nextFocusKey, focusDetails || {});
  this.updateParentsLastFocusedChild(nextFocusKey);
  if (focusDetails && focusDetails.skipScroll) {
    this.clearVisibilityTimers();
  } else {
    this.ensureNodeVisible(this.focusableComponents[nextFocusKey] && this.focusableComponents[nextFocusKey].node);
    this.scheduleEnsureNodeVisible(nextFocusKey);
  }
};

SpatialNavigationService.prototype.getContentFocusKey = function () {
  var component;
  var candidates = [];
  var keys;
  var i;

  if (
    this.lastContentFocusKey &&
    this.isParticipatingFocusableComponent(this.lastContentFocusKey)
  ) {
    component = this.focusableComponents[this.lastContentFocusKey];
    if (component && isNodeInsideContentArea(component.node)) {
      return this.lastContentFocusKey;
    }
  }

  keys = Object.keys(this.focusableComponents);
  for (i = 0; i < keys.length; i += 1) {
    component = this.focusableComponents[keys[i]];
    if (!this.isParticipatingFocusableComponent(component.focusKey)) continue;
    if (!component.node || !isNodeInsideContentArea(component.node)) continue;

    this.updateLayout(component.focusKey);
    candidates.push(component);
  }

  if (!candidates.length) return null;

  sortByLayoutOrder(candidates, this.rtl);
  return candidates[0].focusKey;
};

SpatialNavigationService.prototype.focusContent = function (focusDetails) {
  var contentFocusKey = this.getContentFocusKey();

  if (!contentFocusKey) return false;

  this.setFocus(contentFocusKey, focusDetails || {});
  return true;
};

SpatialNavigationService.prototype.onEnterPress = function (details) {
  var component = this.focusableComponents[this.focusKey];
  if (component && component.focusable !== false) {
    component.onEnterPress(details || {});
  }
};

SpatialNavigationService.prototype.onEnterRelease = function () {
  var component = this.focusableComponents[this.focusKey];
  if (component && component.focusable !== false) {
    component.onEnterRelease();
  }
};

SpatialNavigationService.prototype.onArrowPress = function (direction, details) {
  var component = this.focusableComponents[this.focusKey];
  if (!component || component.focusable === false) return true;
  return component.onArrowPress(direction, details || {});
};

SpatialNavigationService.prototype.findDirectionalSibling = function (component, direction) {
  if (!component) return null;

  var parentKey = component.parentFocusKey;
  var siblings = this.getDirectChildren(parentKey, true);
  var fromLayout = this.updateLayout(component.focusKey);
  var candidates = [];

  for (var i = 0; i < siblings.length; i += 1) {
    var sibling = siblings[i];
    if (sibling.focusKey === component.focusKey) continue;
    var toLayout = this.updateLayout(sibling.focusKey);
    if (!isInDirection(direction, fromLayout, toLayout)) continue;
    if (
      (direction === KEY_UP || direction === KEY_DOWN) &&
      isSameHorizontalBand(fromLayout, toLayout)
    ) {
      continue;
    }
    candidates.push({
      component: sibling,
      score: scoreCandidate(direction, fromLayout, toLayout),
    });
  }

  candidates.sort(function (a, b) {
    return a.score - b.score;
  });

  return candidates.length ? candidates[0].component : null;
};

SpatialNavigationService.prototype.smartNavigate = function (
  direction,
  fromParentFocusKey,
  focusDetails,
  originFocusKey
) {
  var currentKey = fromParentFocusKey || this.focusKey;
  var component = this.focusableComponents[currentKey];
  var navigationOriginKey = originFocusKey || this.focusKey;

  if (!component) {
    this.setFocus(this.getForcedFocusKey() || this.getFirstChildFocusKey(ROOT_FOCUS_KEY));
    return;
  }

  var sibling = this.findDirectionalSibling(component, direction);
  if (sibling) {
    var targetFocusKey = sibling.focusKey;

    if (direction === KEY_UP || direction === KEY_DOWN) {
      var originLayout = this.updateLayout(navigationOriginKey);
      var directionalDescendant = originLayout
        ? this.findDirectionalDescendant(sibling.focusKey, direction, originLayout)
        : null;

      if (directionalDescendant) targetFocusKey = directionalDescendant.focusKey;
    }

    this.setFocus(targetFocusKey, focusDetails || {});
    return;
  }

  var parent = this.focusableComponents[component.parentFocusKey];
  if (!parent) return;

  var boundaryDirections = parent.focusBoundaryDirections || [direction];
  if (parent.isFocusBoundary && contains(boundaryDirections, direction)) {
    return;
  }

  this.smartNavigate(
    direction,
    parent.focusKey,
    focusDetails || {},
    navigationOriginKey
  );
};

SpatialNavigationService.prototype.navigateByDirection = function (direction, focusDetails) {
  if (this.paused || !this.enabled) return;
  if (!contains([KEY_LEFT, KEY_RIGHT, KEY_UP, KEY_DOWN], direction)) return;

  if (this.onArrowPress(direction, { pressedKeys: this.pressedKeys }) === false) {
    return;
  }

  this.smartNavigate(direction, null, focusDetails || {});
};

SpatialNavigationService.prototype.pause = function () {
  this.paused = true;
};

SpatialNavigationService.prototype.resume = function () {
  this.paused = false;
};

SpatialNavigationService.prototype.updateRtl = function (rtl) {
  this.rtl = !!rtl;
};

SpatialNavigationService.prototype.setThrottle = function () {
  return undefined;
};

SpatialNavigationService.prototype.isNativeMode = function () {
  return false;
};

export var SpatialNavigation = new SpatialNavigationService();

export var init = SpatialNavigation.init;
export var destroy = SpatialNavigation.destroy;
export var setFocus = SpatialNavigation.setFocus;
export var focusContent = function (focusDetails) {
  return SpatialNavigation.focusContent(focusDetails || {});
};
export var navigateByDirection = SpatialNavigation.navigateByDirection;
export var updateAllLayouts = SpatialNavigation.updateAllLayouts;
export var pause = SpatialNavigation.pause;
export var resume = SpatialNavigation.resume;
export var setThrottle = SpatialNavigation.setThrottle;
export var setKeyMap = function (keyMap) {
  return SpatialNavigation.setKeyMap(keyMap);
};
export var getCurrentFocusKey = function () {
  return SpatialNavigation.getCurrentFocusKey();
};
export var doesFocusableExist = function (focusKey) {
  return SpatialNavigation.doesFocusableExist(focusKey);
};
export var updateRtl = function (rtl) {
  return SpatialNavigation.updateRtl(rtl);
};

export var FocusContext = React.createContext(ROOT_FOCUS_KEY);
FocusContext.displayName = "FocusContext";

export function useFocusContext() {
  return React.useContext(FocusContext);
}

export function useFocusable(config) {
  var options = config || {};
  var propFocusKey = options.focusKey;
  var generatedFocusKeyRef = React.useRef(propFocusKey || uniqueFocusKey());
  var focusKey = propFocusKey || generatedFocusKeyRef.current;
  var parentFocusKey = useFocusContext();
  var ref = React.useRef(null);
  var stateFocused = React.useState(false);
  var focused = stateFocused[0];
  var setFocused = stateFocused[1];
  var stateHasFocusedChild = React.useState(false);
  var hasFocusedChild = stateHasFocusedChild[0];
  var setHasFocusedChild = stateHasFocusedChild[1];

  var focusable = options.focusable !== false;
  var saveLastFocusedChild = options.saveLastFocusedChild !== false;
  var trackChildren = !!options.trackChildren;
  var autoRestoreFocus = options.autoRestoreFocus !== false;
  var forceFocus = !!options.forceFocus;
  var isFocusBoundary = !!options.isFocusBoundary;
  var focusBoundaryDirections = options.focusBoundaryDirections;
  var preferredChildFocusKey = options.preferredChildFocusKey;
  var extraProps = options.extraProps;
  var onEnterPress = options.onEnterPress || noop;
  var onEnterRelease = options.onEnterRelease || noop;
  var onArrowPress = options.onArrowPress || defaultArrowPress;
  var onFocus = options.onFocus || noop;
  var onBlur = options.onBlur || noop;

  var enterPressHandler = React.useCallback(
    function (details) {
      onEnterPress(extraProps, details || {});
    },
    [onEnterPress, extraProps]
  );

  var enterReleaseHandler = React.useCallback(
    function () {
      onEnterRelease(extraProps);
    },
    [onEnterRelease, extraProps]
  );

  var arrowPressHandler = React.useCallback(
    function (direction, details) {
      return onArrowPress(direction, extraProps, details || {});
    },
    [onArrowPress, extraProps]
  );

  var focusHandler = React.useCallback(
    function (layout, details) {
      onFocus(layout, extraProps, details || {});
    },
    [onFocus, extraProps]
  );

  var blurHandler = React.useCallback(
    function (layout, details) {
      onBlur(layout, extraProps, details || {});
    },
    [onBlur, extraProps]
  );

  var focusSelf = React.useCallback(
    function (focusDetails) {
      SpatialNavigation.setFocus(focusKey, focusDetails || {});
    },
    [focusKey]
  );

  // Register once per focus identity; changing handlers are pushed by the update effect below.
  React.useEffect(
    function () {
      SpatialNavigation.addFocusable({
        focusKey: focusKey,
        node: ref.current,
        parentFocusKey: parentFocusKey || ROOT_FOCUS_KEY,
        preferredChildFocusKey: preferredChildFocusKey,
        onEnterPress: enterPressHandler,
        onEnterRelease: enterReleaseHandler,
        onArrowPress: arrowPressHandler,
        onFocus: focusHandler,
        onBlur: blurHandler,
        onUpdateFocus: function (value) {
          setFocused(!!value);
        },
        onUpdateHasFocusedChild: function (value) {
          setHasFocusedChild(!!value);
        },
        saveLastFocusedChild: saveLastFocusedChild,
        trackChildren: trackChildren,
        isFocusBoundary: isFocusBoundary,
        focusBoundaryDirections: focusBoundaryDirections,
        autoRestoreFocus: autoRestoreFocus,
        forceFocus: forceFocus,
        focusable: focusable,
      });

      return function () {
        SpatialNavigation.removeFocusable({ focusKey: focusKey });
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusKey, parentFocusKey]
  );

  React.useEffect(
    function () {
      SpatialNavigation.updateFocusable(focusKey, {
        node: ref.current,
        parentFocusKey: parentFocusKey || ROOT_FOCUS_KEY,
        preferredChildFocusKey: preferredChildFocusKey,
        saveLastFocusedChild: saveLastFocusedChild,
        trackChildren: trackChildren,
        autoRestoreFocus: autoRestoreFocus,
        forceFocus: forceFocus,
        focusable: focusable,
        isFocusBoundary: isFocusBoundary,
        focusBoundaryDirections: focusBoundaryDirections,
        onEnterPress: enterPressHandler,
        onEnterRelease: enterReleaseHandler,
        onArrowPress: arrowPressHandler,
        onFocus: focusHandler,
        onBlur: blurHandler,
      });
    },
    [
      focusKey,
      parentFocusKey,
      preferredChildFocusKey,
      saveLastFocusedChild,
      trackChildren,
      autoRestoreFocus,
      forceFocus,
      focusable,
      isFocusBoundary,
      focusBoundaryDirections,
      enterPressHandler,
      enterReleaseHandler,
      arrowPressHandler,
      focusHandler,
      blurHandler,
    ]
  );

  return {
    ref: ref,
    focusSelf: focusSelf,
    focused: focused,
    hasFocusedChild: hasFocusedChild,
    focusKey: focusKey,
  };
}

export default SpatialNavigation;
